import {ReactNode, useCallback, useEffect, useMemo, useState} from 'react'
import {Autocomplete, TextField} from '@mui/material'
import {useToast} from '../../../../_fds/layout/components/toast-messages/ToastMessages'
import {AvailabilitySlotModel, AvailabilitySlotType, BaseEmployeeModel} from '../core/_models'
import {startOfWeek, addWeeks} from 'date-fns'
import {
  clearEmployeeAvailability,
  getEmployeeAvailability,
  listEmployeeExceptions,
  replaceEmployeeAvailability,
  replaceEmployeeExceptions,
} from '../core/_requests'
import {
  applyTool,
  apiToSlots,
  buildOpenBands,
  exceptionsToGrid,
  formatSlotRange,
  GridSlot,
  gridToExceptions,
  isoDayOf,
  MINUTES_IN_DAY,
  PaintTool,
  SLOT_TYPE_LABELS,
  slotsEqual,
  toDateKey,
  toggleDayAllDay,
  weekDatesFrom,
  weeklyUnderlay,
} from '../helpers/availability'
import {getPositionDisplayName} from '../helpers/generals'
import {AUTOCOMPLETE_PAPER_PROPS, AUTOCOMPLETE_SX} from '../components/common/autocompleteSkin'
import {useStoreSelector} from '../store'
import {AvailabilityGrid} from './AvailabilityGrid'
import {markAvailabilityChanged} from './markAvailabilityChanged'
import {AvailabilityToolPicker} from './AvailabilityToolPicker'

/**
 * Weekly availability editor — one employee at a time.
 *
 * Availability is date-irrelevant, so this page has no week navigation and
 * ignores the scheduler's selected date entirely. It also keeps its whole
 * editing model in local state rather than the scheduler store: the store is a
 * module-level singleton that outlives the page, and the one existing
 * dateless feature (template mode) needed `if (templateMode) return` guards
 * scattered across six effects to coexist with it. Only the location and the
 * already-fetched employee list are read out, via the selector accessor so
 * unrelated store emits don't re-render the grid.
 */
export const AvailabilityPage = ({layoutToggle}: {layoutToggle?: ReactNode}) => {
  const {showToast} = useToast()

  const {selectedLocationId, employees, hoursOfOperations, hourFormat, week} = useStoreSelector(
    (store) => ({
      selectedLocationId: store.selectedLocationId,
      employees: store.employees,
      hoursOfOperations: store.hoursOfOperations,
      hourFormat: store.hourFormat,
      week: store.week,
    })
  )

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null)
  // 'weekly' is the standing pattern, 'dates' one real week at a time. The two
  // write to different tables and never mix: a dated entry replaces its
  // weekday outright rather than layering on it.
  const [mode, setMode] = useState<'weekly' | 'dates'>('weekly')
  // Weeks forward from the current one. Never negative — the past cannot be
  // edited (the API refuses it too), and there is nothing to plan there.
  const [weekOffset, setWeekOffset] = useState(0)
  const [tool, setTool] = useState<PaintTool>('unavailable')
  const [slots, setSlots] = useState<GridSlot[]>([])
  // The last saved state, for the dirty check and for Reset.
  const [savedSlots, setSavedSlots] = useState<GridSlot[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // Leavers are excluded, not just the hidden. The store's roster is fetched
  // with include_inactive so old schedules can still name whoever worked them,
  // so an inactive employee reaches here unless it is said otherwise — and
  // availability is a statement about future weeks, which someone who has left
  // has none of. Same test the assignee picker uses (AssignEmployeeSelect),
  // and deliberately without Team Members' "Show inactive" toggle: that list
  // is a roster to look things up in, this one is a list to act on.
  // The visible week, in column order, starting on the company's configured
  // first day — so the columns need not begin on a Monday.
  const weekDates = useMemo(() => {
    const start = startOfWeek(addWeeks(new Date(), weekOffset), {
      weekStartsOn: week?.weekStartOn ?? 1,
    })
    return weekDatesFrom(start)
  }, [weekOffset, week?.weekStartOn])

  // The standing pattern, kept loaded in dated mode so it can be drawn faded
  // under the week. Editing it there is not possible — painting writes a date.
  const [weeklySlots, setWeeklySlots] = useState<GridSlot[]>([])

  const schedulableEmployees: BaseEmployeeModel[] = useMemo(
    () =>
      (employees || []).filter(
        (employee: BaseEmployeeModel) =>
          employee.is_active !== false && employee.hide_in_schedule !== true
      ),
    [employees]
  )

  // Pick the first employee once the list arrives, and drop a selection that
  // no longer exists after a location change.
  useEffect(() => {
    if (!schedulableEmployees.length) {
      setSelectedEmployeeId(null)
      return
    }
    const stillThere = schedulableEmployees.some((employee) => employee.id === selectedEmployeeId)
    if (!stillThere) setSelectedEmployeeId(schedulableEmployees[0].id)
  }, [schedulableEmployees, selectedEmployeeId])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!selectedLocationId || !selectedEmployeeId) {
        setSlots([])
        setSavedSlots([])
        setWeeklySlots([])
        return
      }
      setLoading(true)

      // The weekly pattern is fetched either way: it IS the grid in weekly
      // mode, and the faded underlay in dated mode.
      const weekly = apiToSlots(
        (await getEmployeeAvailability(selectedLocationId, selectedEmployeeId))?.slots || []
      )
      // A location/employee/week switch mid-flight must not overwrite the
      // newer selection's grid with this response.
      if (cancelled) return
      setWeeklySlots(weekly)

      if (mode === 'weekly') {
        setSlots(weekly)
        setSavedSlots(weekly)
        setLoading(false)
        return
      }

      const exceptions = await listEmployeeExceptions(
        selectedLocationId,
        selectedEmployeeId,
        toDateKey(weekDates[0]),
        toDateKey(weekDates[6])
      )
      if (cancelled) return
      const dated = exceptionsToGrid(exceptions || [], weekDates)
      setSlots(dated)
      setSavedSlots(dated)
      setLoading(false)
    }
    load()
    return () => {
      cancelled = true
    }
    // weekDates is rebuilt per render; the offset is what actually changes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, selectedEmployeeId, mode, weekOffset, week?.weekStartOn])

  const dirty = useMemo(() => !slotsEqual(slots, savedSlots), [slots, savedSlots])

  // Warn on a full page unload; in-app navigation away is not intercepted
  // (the tab strip uses plain <Link>s, and a router prompt would need a
  // data-router upgrade this app hasn't made).
  useEffect(() => {
    if (!dirty) return undefined
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const handlePaint = useCallback(
    (dayOfWeek: number, startMinute: number, endMinute: number) => {
      setSlots((current) =>
        applyTool(current, dayOfWeek, startMinute, Math.min(endMinute, MINUTES_IN_DAY), tool)
      )
    },
    [tool]
  )

  const handleToggleDay = useCallback((dayOfWeek: number) => {
    setSlots((current) => toggleDayAllDay(current, dayOfWeek))
  }, [])

  const handleSlotNote = useCallback((slot: GridSlot) => {
    // eslint-disable-next-line no-alert
    const note = window.prompt(
      `Note for ${SLOT_TYPE_LABELS[slot.slot_type]} ${formatSlotRange(slot)}`,
      slot.note || ''
    )
    if (note === null) return
    setSlots((current) =>
      current.map((candidate) =>
        candidate.day_of_week === slot.day_of_week &&
        candidate.start_minute === slot.start_minute &&
        candidate.end_minute === slot.end_minute
          ? {...candidate, note: note.trim() || null}
          : candidate
      )
    )
  }, [])

  const handleSave = async () => {
    if (!selectedLocationId || !selectedEmployeeId) return
    setSaving(true)
    try {
      if (mode === 'dates') {
        await replaceEmployeeExceptions(selectedEmployeeId, {
          location_id: selectedLocationId,
          // The window IS the visible week, so saving replaces exactly what
          // was on screen and leaves every other date — history included —
          // untouched.
          date_from: toDateKey(weekDates[0]),
          date_to: toDateKey(weekDates[6]),
          slots: gridToExceptions(slots, weekDates),
        })
      } else {
        await replaceEmployeeAvailability(selectedEmployeeId, {
          location_id: selectedLocationId,
          // 'available' cannot reach here: the tool picker only offers it in
          // dated mode, and the cast is what makes that a compile-time claim
          // rather than a hope.
          slots: slots as AvailabilitySlotModel[],
        })
      }
      setSavedSlots(slots)
      markAvailabilityChanged()
      showToast('Availability', 'Availability saved.', 'success')
    } catch (error) {
      showToast('Availability', 'Could not save availability.', 'danger')
    } finally {
      setSaving(false)
    }
  }

  const handleClear = async () => {
    if (!selectedLocationId || !selectedEmployeeId) return
    setSaving(true)
    try {
      if (mode === 'dates') {
        // An empty save of the same window: only the visible week goes, which
        // is why this cannot reach back and erase history.
        await replaceEmployeeExceptions(selectedEmployeeId, {
          location_id: selectedLocationId,
          date_from: toDateKey(weekDates[0]),
          date_to: toDateKey(weekDates[6]),
          slots: [],
        })
      } else {
        await clearEmployeeAvailability(selectedEmployeeId, selectedLocationId)
      }
      setSlots([])
      setSavedSlots([])
      markAvailabilityChanged()
      showToast(
        'Availability',
        mode === 'dates'
          ? 'This week’s dated entries cleared.'
          : 'Availability cleared — fully available.',
        'success'
      )
    } catch (error) {
      showToast('Availability', 'Could not clear availability.', 'danger')
    } finally {
      setSaving(false)
    }
  }

  // The standing pattern under the visible week, with only the hours the
  // dated entries cover cut out of it — a date overrides its weekday minute
  // by minute, so the rest of that day's rule is still in force and has to
  // stay on screen.
  const underlay = useMemo(
    () => (mode === 'dates' ? weeklyUnderlay(weeklySlots, weekDates, slots) : undefined),
    [mode, slots, weeklySlots, weekDates]
  )

  const openBands = useMemo(() => buildOpenBands(hoursOfOperations), [hoursOfOperations])

  // Searchable picker options. Name first so typing a surname narrows fast —
  // a long roster is the whole reason this is not a plain <select>.
  const employeeOptions = useMemo(
    () =>
      schedulableEmployees.map((employee) => ({
        id: employee.id,
        label: `${employee.last_name}, ${employee.first_name}${
          employee.position ? ` — ${getPositionDisplayName(employee.position)}` : ''
        }`,
      })),
    [schedulableEmployees]
  )
  const selectedOption = employeeOptions.find((option) => option.id === selectedEmployeeId) ?? null

  const selectedEmployee = schedulableEmployees.find(
    (employee) => employee.id === selectedEmployeeId
  )
  const busy = loading || saving
  const noEmployees = !schedulableEmployees.length

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title flex-column'>
          <h3 className='fw-semibold mb-1'>Availability</h3>
          <span className='text-gray-500 fs-7'>
            A repeating weekly pattern — it applies to every week, not a specific date. Anything you
            don't paint counts as available.
          </span>
        </div>
        <div className='card-toolbar'>
          <div className='d-flex align-items-center gap-3'>
            {layoutToggle}
            <Autocomplete
              size='small'
              options={employeeOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, picked) => option.id === picked.id}
              value={selectedOption}
              disabled={noEmployees || busy}
              blurOnSelect
              // Clearing is ignored rather than disabled: the page always
              // edits exactly one employee, so there is no empty state to fall
              // back to, and the controlled value puts the name straight back.
              onChange={(_event, option) => option && setSelectedEmployeeId(option.id)}
              noOptionsText='No matching team members'
              sx={{...AUTOCOMPLETE_SX, width: 250}}
              ListboxProps={{style: {maxHeight: 260}}}
              componentsProps={AUTOCOMPLETE_PAPER_PROPS}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size='small'
                  placeholder={
                    noEmployees ? 'No team members at this location' : 'Search team members…'
                  }
                  inputProps={{...params.inputProps, 'aria-label': 'Employee'}}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className='card-body pt-4'>
        <div className='d-flex flex-wrap justify-content-between align-items-start mb-5 gap-4'>
          <AvailabilityToolPicker
            value={tool}
            disabled={busy}
            onChange={setTool}
            dated={mode === 'dates'}
          />

          <div className='d-flex align-items-center gap-2 pt-6'>
            {dirty && <span className='badge badge-light-warning'>Unsaved changes</span>}
            <button
              type='button'
              className={`btn btn-sm ${
                mode === 'dates' ? 'btn-primary' : 'btn-light-primary'
              } d-inline-flex align-items-center`}
              aria-pressed={mode === 'dates'}
              data-testid='availability-mode-toggle'
              disabled={busy}
              title={
                mode === 'dates'
                  ? 'Back to the standing weekly pattern'
                  : 'Set availability for specific dates instead'
              }
              onClick={() => {
                // Unsaved paint belongs to the mode it was painted in, and the
                // two write to different tables — so a switch drops it rather
                // than carrying strokes across. The load effect refills both.
                setMode(mode === 'dates' ? 'weekly' : 'dates')
                setWeekOffset(0)
                setTool('unavailable')
              }}
            >
              <i className='bi bi-calendar-event fs-8 me-2' aria-hidden='true' />
              {mode === 'dates' ? 'Weekly pattern' : 'One-time availability'}
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light'
              disabled={busy || !dirty}
              onClick={() => setSlots(savedSlots)}
            >
              Reset
            </button>
            <button
              type='button'
              className='btn btn-sm btn-light-danger'
              disabled={busy || (!slots.length && !savedSlots.length)}
              onClick={handleClear}
            >
              Clear all
            </button>
            <button
              type='button'
              className='btn btn-sm btn-primary'
              disabled={busy || !dirty || !selectedEmployeeId}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {noEmployees ? (
          <div className='text-center text-gray-500 py-10'>No team members at this location.</div>
        ) : (
          <>
            {mode === 'dates' && (
              <div className='d-flex align-items-center justify-content-center gap-3 mb-4'>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-light'
                  data-testid='availability-week-prev'
                  // Nothing to plan in the past, and the API refuses to write
                  // there — so the current week is the floor rather than a
                  // step that fails on save.
                  disabled={busy || weekOffset === 0}
                  aria-label='Previous week'
                  title={weekOffset === 0 ? 'This is the current week' : 'Previous week'}
                  onClick={() => setWeekOffset((offset) => Math.max(0, offset - 1))}
                >
                  <i className='bi bi-chevron-left' aria-hidden='true' />
                </button>
                <span
                  className='fw-bold text-gray-800 text-nowrap'
                  data-testid='availability-week-label'
                >
                  {weekDates[0].toLocaleDateString(undefined, {day: 'numeric', month: 'short'})} –{' '}
                  {weekDates[6].toLocaleDateString(undefined, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                  {weekOffset === 0 && (
                    <span className='badge badge-light-primary ms-3'>This week</span>
                  )}
                </span>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-light'
                  data-testid='availability-week-next'
                  disabled={busy}
                  aria-label='Next week'
                  title='Next week'
                  onClick={() => setWeekOffset((offset) => offset + 1)}
                >
                  <i className='bi bi-chevron-right' aria-hidden='true' />
                </button>
              </div>
            )}

            {loading && <div className='text-gray-500 fs-7 mb-2'>Loading availability…</div>}
            <AvailabilityGrid
              slots={slots}
              tool={tool}
              use12Hour={hourFormat === '12'}
              disabled={busy || !selectedEmployeeId}
              openBands={openBands}
              onPaint={handlePaint}
              onToggleDay={handleToggleDay}
              onSlotClick={handleSlotNote}
              columnDates={mode === 'dates' ? weekDates : undefined}
              underlaySlots={underlay}
            />
            {selectedEmployee && (
              <div className='fs-8 text-gray-500 mt-1'>
                Editing {selectedEmployee.first_name} {selectedEmployee.last_name} — {slots.length}{' '}
                {slots.length === 1 ? 'entry' : 'entries'}. Shaded rows are outside this location's
                opening hours.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export type {AvailabilitySlotType}
